import React from 'react';
import EdgeFadeScroller from '../common/EdgeFadeScroller/EdgeFadeScroller';
import HorizontalCarousel from '../HorizontalCarousel';
import ListingCardSkeleton from './ListingCardSkeleton';

interface ListingCardRowProps {
  /** Section heading, e.g. "Sellers here". A node so a caller can pass <Trans>. */
  title: React.ReactNode;
  /** Plain-text heading for the carousel's aria-label, when `title` is a node. */
  ariaLabel?: string;
  /** The building view heads its rows with h3, the listing page with h2. */
  headingLevel?: 2 | 3;
  /** The cards themselves. Each page builds its own — a building listing and an
   *  area interest need different handlers — so only the row chrome is shared. */
  cards: React.ReactNode[];
  /** Desktop gets the drag/wheel scroller, mobile the snap carousel. The caller
   *  decides, because the two pages already track their own breakpoint. */
  isDesktop: boolean;
  /** First page still in flight: show skeletons rather than an empty row, so the
   *  section keeps its height and nothing jumps when the data lands. */
  loading?: boolean;
  /** More pages exist; the row asks for one when scrolled near its right edge. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** A later page in flight — skeletons trail the real cards. */
  loadingMore?: boolean;
  /** Optional card pinned after the list, e.g. the building view's intent CTA. */
  trailing?: React.ReactNode;
  /** Section class, so each page keeps its own spacing. */
  className?: string;
  /** Track class, so each page keeps its own card sizing and gaps. Passing the
   *  existing per-page class is what makes this swap purely structural. */
  trackClassName?: string;
  /** Heading class, same reason. */
  titleClassName?: string;
}

// How many placeholders stand in for a page that has not arrived. Three fills a
// desktop row without pretending to know the real count.
const SKELETON_COUNT = 3;

/**
 * A titled horizontal row of big listing cards, with skeleton loading and
 * scroll-to-load-more.
 *
 * The building page and the listing page both render this same row and had
 * drifted into two copies of the chrome — the breakpoint split, the section
 * markup, the empty-state rule. The cards stay with the caller; everything
 * around them lives here.
 *
 * Renders nothing when there is no content and nothing pending, so a caller can
 * mount it unconditionally.
 */
const ListingCardRow: React.FC<ListingCardRowProps> = ({
  title,
  cards,
  isDesktop,
  loading = false,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  trailing,
  className,
  trackClassName,
  titleClassName,
  ariaLabel,
  headingLevel = 2,
}) => {
  if (cards.length === 0 && !loading && !trailing) return null;

  // One element per placeholder rather than one element rendering several: the
  // mobile carousel makes a snap slide per child, so a single fragment of three
  // would swipe as one slide while the loaded row swipes as three.
  const skeletons = (count: number, key: string) =>
    Array.from({ length: count }, (_, i) => (
      <ListingCardSkeleton key={`${key}-${i}`} />
    ));

  const content: React.ReactNode[] = loading
    ? skeletons(SKELETON_COUNT, 'initial')
    : [
        ...cards,
        ...(loadingMore ? skeletons(1, 'more') : []),
        ...(trailing ? [trailing] : []),
      ];

  // Only arm the loader when there is more to fetch and nothing already in
  // flight, so a scroll at the edge cannot queue duplicate pages.
  const reachEnd =
    hasMore && !loadingMore && onLoadMore ? onLoadMore : undefined;

  return (
    <section className={className}>
      {React.createElement(
        `h${headingLevel}`,
        { className: titleClassName },
        title,
      )}
      {isDesktop ? (
        <EdgeFadeScroller
          className={trackClassName}
          onReachEnd={reachEnd}
          showDots
        >
          {content}
        </EdgeFadeScroller>
      ) : (
        <HorizontalCarousel
          ariaLabel={ariaLabel ?? (typeof title === 'string' ? title : '')}
          onReachEnd={reachEnd}
        >
          {content}
        </HorizontalCarousel>
      )}
    </section>
  );
};

export default ListingCardRow;
